import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	PinReportApiResponse,
	PinReportPayload,
	ReportConfig,
	ReportsMetaDataResponse,
} from 'src/types/reportingTypes';
import { pushToast } from '../reportingSlice';
import { fetchReportsMetaData } from './fetchReportsMetaData';
import { pinReport } from './pinReport';

export type DeleteReportResult = {
	reportId?: string;
	name?: string;
	type?: string;
	config?: ReportConfig;
};

export const deleteReport = createAsyncThunk<
	DeleteReportResult,
	deleteReport,
	{ rejectValue: string }
>(
	'reporting/deleteReport',
	async (
		{ api, id, reportId, nextReportId },
		{ dispatch, rejectWithValue },
	) => {
		try {
			const response = await api.deleteUnifiedReportById(id, reportId);
			if (response) {
				dispatch(
					pushToast({
						type: 'success',
						message: 'Report deleted successfully',
					}),
				);
				const metaData = await dispatch(
					fetchReportsMetaData({ api, id }),
				).unwrap();

				const reports = metaData.reports?.reports || [];
				const pinnedReportId = metaData.reports?.pinnedReport || null;
				let selectedReport = null;

				if (pinnedReportId === reportId) {
					// If the deleted report is the pinned one, select Barracuda report
					selectedReport = reports.find(
						(r) => r.type === 'Barracuda',
					);
					if (selectedReport) {
						// Pin the Barracuda report
						dispatch(
							pinReport({
								api,
								id,
								reqPayload: {
									reportId: selectedReport.reportId,
								},
							}),
						);
					}
				} else {
					if (reports.length === 1) {
						selectedReport = reports[0];
					} else {
						selectedReport = reports.find(
							(r) => r.reportId === nextReportId,
						);
						if (!selectedReport && reports.length > 0) {
							selectedReport = reports[0];
						}
					}
				}

				return {
					reportId: selectedReport?.reportId,
					name: selectedReport?.name,
					type: selectedReport?.type,
					config: selectedReport?.config,
				};
			}
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			dispatch(
				pushToast({
					type: 'error',
					message:
						'Error on deleting report. Please try again after some time.',
				}),
			);
			return rejectWithValue(message);
		}
	},
);

type deleteReport = {
	api: {
		deleteUnifiedReportById: (
			id: string,
			reportId: string,
		) => Promise<boolean>;
		getUnifiedReportingMetaData: (
			id: string,
		) => Promise<ReportsMetaDataResponse>;
		pinReport: (id: string, reqPayload: PinReportPayload) => Promise<PinReportApiResponse>;
	};
	id: string;
	reportId: string;
	nextReportId: string | null;
};
